import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';

import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getFormBundleForLoanByLoanIdAction } from 'store/forms/actions';
import {
  getApplicationConsentsAction,
  getLoanBundleHistoryAction,
  getLoanKeyDatesAction,
} from 'store/loanApplications/actions';
import { selectIsLoanLocked } from 'store/loanApplications/selectors';

import Conditions from '../loanConditions/Conditions';

import Consent from './consent';
import DocumentExpirationDates from './documentExpirationDates';
import General from './general/General';
import KeyDates from './keyDates';
import LoanBundleHistory from './loanBundleHistory';

const LoanBundleHistoryAndConsent = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const { applicationId } = useParams();

  const isLoanLocked = useSelector(selectIsLoanLocked);
  const { isManageAllowed: isUnderwritingAccessAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_UNDERWRITING),
  );
  const { isViewAllowed: isConditionsAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_CONDITIONS),
  );

  const isGeneralEnabled = isLoanLocked && isUnderwritingAccessAllowed;

  useEffectSafeStrictMode(() => {
    if (!loanId) {
      return;
    }

    setIsLoading(true);

    Promise.all([
      dispatch(getLoanBundleHistoryAction({ loanId, applicationId })),
      dispatch(getFormBundleForLoanByLoanIdAction({ loanId })),
      dispatch(getApplicationConsentsAction({ applicationId })),
      dispatch(getLoanKeyDatesAction({ loanId })),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [applicationId, dispatch, loanId, setIsLoading]);

  return (
    <div className="mt-10">
      {isGeneralEnabled && <General isLoading={isLoading} setIsLoading={setIsLoading} />}
      {isUnderwritingAccessAllowed && <DocumentExpirationDates />}
      {isConditionsAllowed && <Conditions />}
      <Consent isLoading={isLoading} />
      <KeyDates isLoading={isLoading} />
      <LoanBundleHistory isLoading={isLoading} />
    </div>
  );
};

LoanBundleHistoryAndConsent.propTypes = {
  ...WithLoadingPropTypes,
};

export default withLoading(LoanBundleHistoryAndConsent);
